import { debounced } from "./debounce.js";
import { wireExamples } from "./examples.js";
import { findScriptElements, type ScriptElements } from "./script-elements.js";
import { ScriptLoading, type ScriptResources } from "./script-loading.js";
import { wireScriptCopies } from "./script-panels.js";
import { ScriptView } from "./script-view.js";
import { wireShareLink } from "./share-link.js";
import { readUrlState, rememberUrl } from "./url-state.js";

/**
 * The 简体 and 繁體 converter: Chinese in, Chinese in every script out.
 *
 * The second tool here that needs a dictionary, and it needs it for a reason
 * worth stating on the page: simplification merged characters, so converting
 * back means knowing which word a character is in and how that word is read.
 * 头发 is 頭髮 and 出发 is 出發, and nothing about 发 says which.
 *
 * There is nothing to configure. 简体, Taiwan's 繁體 and Hong Kong's 繁體 all
 * come back at once, so the only state the page has is the text.
 */

/**
 * Start the tool on a container the template marked for it.
 *
 * Returns without doing anything if the container is not the shape this
 * expects or does not say where the dictionary is, which is what keeps a
 * markup change from throwing on every page the bundle loads on.
 */
export function startScriptTool(container: HTMLElement): void {
  const dictionaryUrl = container.dataset["dictionary"];
  const elements = findScriptElements(container);

  if (dictionaryUrl === undefined || elements === undefined) {
    return;
  }

  new ScriptTool(container, elements, dictionaryUrl).start();
}

/**
 * The tool's state: a dictionary, a set of tables and a text.
 */
class ScriptTool {
  readonly #container: HTMLElement;
  readonly #elements: ScriptElements;
  readonly #loading: ScriptLoading;
  readonly #view: ScriptView;

  readonly #scheduleConvert = debounced(() => {
    this.#convert();
  });

  #resources: ScriptResources | undefined;

  constructor(
    container: HTMLElement,
    elements: ScriptElements,
    dictionaryUrl: string,
  ) {
    this.#container = container;
    this.#elements = elements;
    this.#loading = new ScriptLoading(dictionaryUrl, elements.status);
    this.#view = new ScriptView(elements);

    const fromUrl = readUrlState(location.search);
    if (fromUrl.text !== undefined) {
      elements.input.value = fromUrl.text;
    }
  }

  #convert(): void {
    const resources = this.#resources;
    if (resources === undefined) {
      return;
    }

    const text = this.#elements.input.value;

    if (text.trim() === "") {
      this.#view.clear();
    } else {
      this.#view.show(resources, text);
    }

    rememberUrl({ text });
  }

  async #load(): Promise<void> {
    const resources = await this.#loading.load();

    if (resources !== undefined) {
      this.#resources = resources;
      this.#convert();
    }
  }

  start(): void {
    const { input, panels } = this.#elements;

    input.addEventListener("input", this.#scheduleConvert);

    wireScriptCopies(panels);

    wireShareLink(this.#container, () => ({ text: input.value }));

    wireExamples(this.#container, (text) => {
      input.value = text;
      this.#convert();
    });

    void this.#load();
  }
}
